import React, { useEffect, useState, useCallback, useRef } from "react";

import {
  Box,
  Button,
  IconButton,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  ScreenShareOutlined,
  AspectRatio as AspectRatioIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Stage, Layer } from "react-konva";
import { useParams } from "react-router";

import {
  VideoResponse,
  ExportVideoResolution,
  ExportVideoCompressionLevel,
  GifOverlay,
  TextOverlay,
} from "../../EditShortVideo/types";
import { IElement } from "./components/Element/types/IElement";
import Row from "./components/Row";
import Ruler from "./components/Ruler";
import VerticalLine from "./components/VerticalLine/VerticalLine";
import { trackEvent } from "../../../analytics";
import Controls from "./components/Controls";
import Zoom from "./components/Zoom";
import ExportVideoModal from "../../EditShortVideo/components/ExportVideoModal";
import { editShortVideoService } from "../../EditShortVideo/services";
import AspectRatioSelector, {
  AspectRatio,
  ASPECT_RATIOS,
} from "../../EditShortVideo/components/AspectRatioSelector";
import { editorModalTheme } from "../../EditShortVideo/theme/editorModalTheme";
import {
  RULER_HEIGHT_IN_PX,
  ROW_HEIGHT_IN_PX,
  TIMELINE_FIXED_HEIGHT_IN_PX,
} from "./constants";

const minimumTime = 60000; // 60 seconds in milliseconds

interface TimelineProps {
  video: VideoResponse;
  /**
   * When false (viewer role), editing controls such as the Format button are
   * hidden. Playback and Export remain available.
   */
  canEdit?: boolean;
  currentTimeInMilliseconds: number;
  isPlaying: boolean;
  onPlayPause: () => void;
  setCurrentTimeInMilliseconds: (time: number) => void;
  elements: IElement[];
  setElements: (elements: IElement[]) => void;
  setIsExporting: (isExporting: boolean) => void;
  currentAspectRatio?: AspectRatio;
  onAspectRatioChange?: (ratio: AspectRatio) => void;
  currentSubtitleStyles?: any;
  selectedElementId?: number | null;
  onElementSelect?: (element: IElement | null) => void;
  onElementDelete?: (element: IElement) => void;
  onElementDuplicate?: (element: IElement) => void;
  onElementTimeChange?: (element: IElement) => void;
  gifOverlays?: GifOverlay[];
  textOverlays?: TextOverlay[];
}

/**
 * Resolves overlaps for a single element against all others in the same row.
 * Pushes the element to the right of any overlapping element.
 */
function resolveOverlaps(element: IElement, allElements: IElement[]): IElement {
  const sameRowElements = allElements
    .filter((el) => el.id !== element.id && el.row === element.row)
    .sort((a, b) => a.start - b.start);

  const resolved = { ...element };
  let hasOverlap = true;
  let iterations = 0;

  while (hasOverlap && iterations < 50) {
    const overlapState = { found: false };
    sameRowElements.forEach((other) => {
      if (
        resolved.start < other.start + other.width &&
        resolved.start + resolved.width > other.start
      ) {
        resolved.start = other.start + other.width;
        overlapState.found = true;
      }
    });
    hasOverlap = overlapState.found;
    iterations += 1;
  }

  return resolved;
}

interface ContextMenuState {
  element: IElement;
  x: number;
  y: number;
}

export default function Timeline({
  video,
  canEdit = true,
  currentTimeInMilliseconds,
  isPlaying,
  onPlayPause,
  setCurrentTimeInMilliseconds,
  elements,
  setElements,
  setIsExporting,
  currentAspectRatio,
  onAspectRatioChange,
  currentSubtitleStyles,
  selectedElementId,
  onElementSelect,
  onElementDelete,
  onElementDuplicate,
  onElementTimeChange,
  gifOverlays = [],
  textOverlays = [],
}: TimelineProps) {
  const { videoId } = useParams();
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showFormatModal, setShowFormatModal] = useState<boolean>(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const maxTime = Math.max(
    minimumTime,
    video.duration_in_milliseconds,
    ...elements.map((el) => el.start + el.width),
  );
  const totalMilliseconds = Math.ceil(maxTime);

  const pixelsPerMillisecond =
    (window.innerWidth / totalMilliseconds) * zoomLevel;

  const timelineWidth = totalMilliseconds * pixelsPerMillisecond;

  const handleZoomChange = (zoom: number) => {
    setZoomLevel(Math.min(5, Math.max(0.1, zoom)));
  };

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const newZoomLevel = zoomLevel - e.deltaY * 0.001;
        handleZoomChange(newZoomLevel);
      }
    };

    window.addEventListener("wheel", handleWheel);
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, [zoomLevel]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Delete selected element
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedElementId != null
      ) {
        const selectedEl = elements.find((el) => el.id === selectedElementId);
        if (selectedEl && selectedEl.type !== "video" && onElementDelete) {
          e.preventDefault();
          onElementDelete(selectedEl);
        }
      }
      // Space to play/pause
      if (e.key === " " && e.target === document.body) {
        e.preventDefault();
        onPlayPause();
      }
      // Escape to deselect
      if (e.key === "Escape" && onElementSelect) {
        onElementSelect(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    selectedElementId,
    elements,
    onElementDelete,
    onPlayPause,
    onElementSelect,
  ]);

  const rows = elements.reduce((acc, element) => {
    const { row } = element;
    if (!acc[row - 1]) {
      acc[row - 1] = [];
    }
    acc[row - 1].push(element);
    return acc;
  }, [] as IElement[][]);

  // always add an empty row
  rows.push([]);

  const timelineHeight = RULER_HEIGHT_IN_PX + rows.length * ROW_HEIGHT_IN_PX;

  const handleElementChange = (element: IElement) => {
    let updatedElements = elements.map((el) =>
      el.id === element.id ? element : el,
    );

    const { isDragging } = element;
    const { isResizing } = element;

    // Resolve overlaps on drop (not during drag)
    if (!isDragging && !isResizing) {
      const resolved = resolveOverlaps(element, updatedElements);
      // Never let an element sit past the end of the video.
      if (element.type !== "video" && video.duration_in_milliseconds > 0) {
        const maxStart = Math.max(
          0,
          video.duration_in_milliseconds - resolved.width,
        );
        resolved.start = Math.min(resolved.start, maxStart);
      }
      updatedElements = updatedElements.map((el) =>
        el.id === element.id
          ? { ...resolved, isDragging: false, isResizing: false }
          : el,
      );

      // Notify parent about time change for bidirectional sync
      const finalElement = updatedElements.find((el) => el.id === element.id);
      if (finalElement && onElementTimeChange) {
        onElementTimeChange(finalElement);
      }
    }

    setElements(updatedElements);
  };

  const handleElementSelect = useCallback(
    (element: IElement) => {
      if (onElementSelect) {
        if (selectedElementId === element.id) {
          onElementSelect(null);
        } else {
          onElementSelect(element);
        }
      }
    },
    [selectedElementId, onElementSelect],
  );

  const handleContextMenu = useCallback(
    (element: IElement, x: number, y: number) => {
      setContextMenu({ element, x, y });
      // Also select the element
      if (onElementSelect) {
        onElementSelect(element);
      }
    },
    [onElementSelect],
  );

  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleContextMenuDelete = useCallback(() => {
    if (
      contextMenu &&
      onElementDelete &&
      contextMenu.element.type !== "video"
    ) {
      onElementDelete(contextMenu.element);
    }
    setContextMenu(null);
  }, [contextMenu, onElementDelete]);

  const handleContextMenuDuplicate = useCallback(() => {
    if (
      contextMenu &&
      onElementDuplicate &&
      contextMenu.element.type !== "video"
    ) {
      onElementDuplicate(contextMenu.element);
    }
    setContextMenu(null);
  }, [contextMenu, onElementDuplicate]);

  const handleVerticalLineDrag = (x: number) => {
    setCurrentTimeInMilliseconds(Math.max(0, x / pixelsPerMillisecond));
  };

  const handleStageClick = (e: any) => {
    // Only seek if clicking on empty space (not on an element)
    if (e.target === e.target.getStage() || e.target.getLayer() === e.target) {
      const stage = e.target.getStage();
      const pos = stage.getPointerPosition();
      if (pos) {
        const newTime = pos.x / pixelsPerMillisecond;
        setCurrentTimeInMilliseconds(Math.max(0, newTime));
      }
      // Deselect when clicking empty space
      if (onElementSelect) {
        onElementSelect(null);
      }
    }
  };

  const handleExportVideo = async (
    videoResolution: ExportVideoResolution,
    compressionLevel: ExportVideoCompressionLevel,
    fps: number,
    includeSubtitle: boolean,
    font?: string,
  ): Promise<void> => {
    if (videoId) {
      trackEvent("export_video_started");
      // Show spinner
      setIsExporting(true);
      // Save overlays to the server before triggering export
      await editShortVideoService.updateShortVideo(videoId, {
        gifOverlays,
        textOverlays,
        videoDurationMs: video.duration_in_milliseconds,
      });
      await editShortVideoService.exportShortVideo(videoId, {
        compression_level: compressionLevel,
        video_resolution: videoResolution,
        fps,
        include_subtitle: includeSubtitle,
        font,
      });
      setShowExportModal(false);
    }
  };

  // Apply selection state to elements for rendering
  const elementsWithSelection = elements.map((el) => ({
    ...el,
    isSelected: el.id === selectedElementId,
  }));

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Zoom zoomLevel={zoomLevel} onZoomChange={handleZoomChange} />
        </Box>

        <Controls
          isPlaying={isPlaying}
          currentTimestamp={currentTimeInMilliseconds}
          durationInMilliseconds={video.duration_in_milliseconds}
          onPlayPause={onPlayPause}
          onPrevious={() =>
            setCurrentTimeInMilliseconds(
              Math.max(0, currentTimeInMilliseconds - 100),
            )
          }
          onNext={() =>
            setCurrentTimeInMilliseconds(
              Math.min(
                video.duration_in_milliseconds,
                currentTimeInMilliseconds + 100,
              ),
            )
          }
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
          }}
        >
          {canEdit && (
            <Button
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": {
                  borderColor: "text.secondary",
                  color: "text.primary",
                },
              }}
              size="small"
              onClick={() => setShowFormatModal(true)}
              variant="outlined"
              title="Video Format"
              startIcon={<AspectRatioIcon />}
            >
              Format
            </Button>
          )}
          <Button
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
            }}
            disableElevation
            size="small"
            onClick={() => setShowExportModal(true)}
            variant="contained"
            title="Export Video"
            startIcon={<ScreenShareOutlined />}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Timeline body with row labels */}
      <Box
        sx={{
          display: "flex",
          position: "relative",
          height: TIMELINE_FIXED_HEIGHT_IN_PX,
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        {/* Scrollable timeline area */}
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            overflowX: "scroll",
            overflowY: "auto",
            position: "relative",
            py: 1,
          }}
          style={{ cursor: "pointer" }}
        >
          <Box sx={{ width: timelineWidth, height: timelineHeight }}>
            <Stage
              width={timelineWidth}
              height={timelineHeight}
              onClick={handleStageClick}
            >
              <Layer>
                <Ruler
                  totalMilliseconds={maxTime}
                  pixelsPerMillisecond={pixelsPerMillisecond}
                />
                {rows.map((row, rowIndex) => (
                  <Row
                    key={`row-${rowIndex + 1}`}
                    elements={elementsWithSelection.filter(
                      (el) => el.row === rowIndex + 1,
                    )}
                    allElements={elementsWithSelection}
                    rowIndex={rowIndex}
                    onElementChange={handleElementChange}
                    onElementSelect={handleElementSelect}
                    onContextMenu={handleContextMenu}
                    pixelsPerMillisecond={pixelsPerMillisecond}
                    numberOfRows={rows.length}
                    playheadTimeMs={currentTimeInMilliseconds}
                    timelineWidth={timelineWidth}
                    videoDurationMs={video.duration_in_milliseconds}
                  />
                ))}
                <VerticalLine
                  x={currentTimeInMilliseconds * pixelsPerMillisecond}
                  onDrag={handleVerticalLineDrag}
                  height={timelineHeight}
                />
              </Layer>
            </Stage>
          </Box>

          {/* Context Menu */}
          {contextMenu && (
            <ClickAwayListener onClickAway={handleContextMenuClose}>
              <Paper
                elevation={8}
                sx={{
                  position: "absolute",
                  top: contextMenu.y,
                  left: contextMenu.x,
                  zIndex: 1000,
                  minWidth: 160,
                  py: 0.5,
                  borderRadius: 2,
                }}
              >
                <MenuItem
                  onClick={handleContextMenuDuplicate}
                  disabled={contextMenu.element.type === "video"}
                  dense
                >
                  <ListItemIcon>
                    <DuplicateIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Duplicate</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={handleContextMenuDelete}
                  disabled={contextMenu.element.type === "video"}
                  dense
                  sx={{ color: "error.main" }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              </Paper>
            </ClickAwayListener>
          )}
        </Box>
      </Box>

      <ExportVideoModal
        open={showExportModal}
        handleClose={() => setShowExportModal(false)}
        handleExport={handleExportVideo}
        currentAspectRatio={currentAspectRatio}
        currentFont={
          currentSubtitleStyles?.font || video?.subtitle_styles?.font
        }
      />

      {/* Format Modal */}
      {showFormatModal && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
          onClick={() => setShowFormatModal(false)}
        >
          <ThemeProvider theme={editorModalTheme}>
            <Box
              sx={{
                maxWidth: 700,
                width: "100%",
                maxHeight: "80vh",
                overflow: "auto",
                position: "relative",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                onClick={() => setShowFormatModal(false)}
                aria-label="Close"
                sx={{
                  position: "absolute",
                  top: 24,
                  right: 12,
                  zIndex: 1,
                  color: "text.secondary",
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.14)",
                    color: "text.primary",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              <Box sx={{ pt: 2 }}>
                <AspectRatioSelector
                  selectedRatio={currentAspectRatio || ASPECT_RATIOS[0]}
                  onRatioChange={(ratio) => {
                    if (onAspectRatioChange) {
                      onAspectRatioChange(ratio);
                    }
                    setShowFormatModal(false);
                  }}
                />
              </Box>
            </Box>
          </ThemeProvider>
        </Box>
      )}
    </>
  );
}
